import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { PaginatedCollection } from '../app/models/PaginatedCollection';
import { Collection } from '../app/models/catalog';
import {
  ComposedLegalDocument,
  LegalAcceptance,
  LegalDocument,
  LegalDocumentCreateRequest,
  LegalDocumentEditRequest,
  LegalDocumentType,
  LegalPendingUser,
  LegalPlatform
} from '../app/models/legal-document';

/**
 * The Terms of Use, Privacy Policy and DPA, versioned, one set per app plus the General part the
 * products share.
 * Drafts are edited freely; publishing one makes it the version every user of that app must accept
 * before they can go on.
 */
@Injectable({
  providedIn: 'root'
})
export class LegalDocumentService {
  private readonly url = `${environment.api.baseUrl}${environment.api.endpoints.legalDocuments}`;

  constructor(private http: HttpClient) {}

  list(platform?: LegalPlatform, type?: LegalDocumentType): Observable<Collection<LegalDocument>> {
    let params = new HttpParams();
    if (platform) {
      params = params.set('platform', platform);
    }
    if (type) {
      params = params.set('type', type);
    }
    return this.http.get<Collection<LegalDocument>>(this.url, { params });
  }

  /** The app's document as it will read once the pending drafts are published. */
  preview(platform: LegalPlatform, type: LegalDocumentType): Observable<ComposedLegalDocument> {
    return this.http.get<ComposedLegalDocument>(`${this.url}/composed`, { params: { platform, type } });
  }

  get(id: number | string): Observable<LegalDocument> {
    return this.http.get<LegalDocument>(`${this.url}/${id}`);
  }

  create(request: LegalDocumentCreateRequest): Observable<LegalDocument> {
    return this.http.post<LegalDocument>(this.url, request);
  }

  /** A new draft starting from this version's title and text. */
  copy(id: number | string): Observable<LegalDocument> {
    return this.http.post<LegalDocument>(`${this.url}/${id}/copy`, null);
  }

  update(id: number | string, request: LegalDocumentEditRequest): Observable<LegalDocument> {
    return this.http.put<LegalDocument>(`${this.url}/${id}`, request);
  }

  delete(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.url}/${id}`);
  }

  publish(id: number | string): Observable<LegalDocument> {
    return this.http.post<LegalDocument>(`${this.url}/${id}/publish`, null);
  }

  acceptances(id: number | string, page: number, size: number): Observable<PaginatedCollection<LegalAcceptance>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<PaginatedCollection<LegalAcceptance>>(`${this.url}/${id}/acceptances`, { params });
  }

  pending(id: number | string, page: number, size: number): Observable<PaginatedCollection<LegalPendingUser>> {
    const params = new HttpParams().set('page', page).set('size', size);
    return this.http.get<PaginatedCollection<LegalPendingUser>>(`${this.url}/${id}/pending`, { params });
  }

  /** Fetched through HttpClient, not linked, so the request carries the bearer token. */
  exportAcceptances(id: number | string): Observable<Blob> {
    return this.http.get(`${this.url}/${id}/acceptances.csv`, { responseType: 'blob' });
  }
}
